import type { OrderDocumentType } from '@api/orders';
import type {
  SpineLinkBlocked,
  SpineLinkBlockedIssue,
  SpineLinkBlockedKind,
} from '@/react/utils/spineLinkError';
import { pluralizeKey } from '@/react/utils/i18n';

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

export type SpineBlockedDocument = OrderDocumentType | 'xero_invoice';

const DOCUMENT_NAME_KEY: Record<SpineBlockedDocument, string> = {
  quotation: 'order_actions.spine_blocked_generate_document_quotation',
  confirmation: 'order_actions.spine_blocked_generate_document_confirmation',
  delivery_order: 'order_actions.spine_blocked_generate_document_delivery_order',
  xero_invoice: 'order_actions.spine_blocked_generate_document_xero_invoice',
};

const listFormatter = new Intl.ListFormat('en-SG', { style: 'long', type: 'conjunction' });

// Both dialogs open over an order with many events, so the rep has no other way
// to tell which one holds the dead link.
export const formatEventIds = (eventIds: string[]): string => listFormatter.format(eventIds);

function blockedFact(issue: SpineLinkBlockedIssue, t: TranslateFn): string {
  return t(pluralizeKey(`order_actions.spine_blocked_fact_${issue.kind}`, issue.eventIds.length), {
    event_ids: formatEventIds(issue.eventIds),
  });
}

// The client owns every rep-facing blocked sentence. It states every fact,
// then names the attempted action once. `message` is the compatibility fallback
// for a server build that has not started sending event ids yet.
function blockedMessage(
  actionKey: string,
  blocked: Pick<SpineLinkBlocked, 'issues' | 'message'>,
  params: Record<string, string | number> = {},
  t: TranslateFn,
): string {
  const eventCount = blocked.issues.reduce((count, issue) => count + issue.eventIds.length, 0);
  if (eventCount === 0) return blocked.message ?? '';

  const facts = blocked.issues.map((issue) => blockedFact(issue, t));
  const action = t(pluralizeKey(actionKey, eventCount), params);
  return [...facts, action].join(' ');
}

export function buildSpineBlockedCopy(
  blocked: Pick<SpineLinkBlocked, 'issues' | 'message'>,
  documentType: SpineBlockedDocument,
  t: TranslateFn,
): { title: string; message: string } {
  const document = t(DOCUMENT_NAME_KEY[documentType]);

  return {
    title: t('order_actions.spine_blocked_generate_title', { document_type: document }),
    message: blockedMessage(
      'order_actions.spine_blocked_generate_action',
      blocked,
      { document_type: document },
      t,
    ),
  };
}

export function buildSpineBlockedConfirmCopy(
  blocked: Pick<SpineLinkBlocked, 'issues' | 'message'>,
  t: TranslateFn,
): { title: string; message: string } {
  return {
    title: t('order_actions.spine_blocked_confirm_title'),
    message: blockedMessage('order_actions.spine_blocked_confirm_action', blocked, {}, t),
  };
}

export type { SpineLinkBlockedKind };
